import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../utils/functions/api';
import { showAlert } from '../../utils/functions/globalDialog';

const OPTIONAL_SCOPES = [
  { key: 'salary', label: 'เงินเดือน' },
  { key: 'bank_account', label: 'เลขบัญชีธนาคาร' },
  { key: 'tax_id', label: 'เลขประจำตัวผู้เสียภาษี' },
  { key: 'address', label: 'ที่อยู่' },
  { key: 'id_card_image', label: 'รูปบัตรประชาชน' },
  { key: 'documents', label: 'เอกสารแนบ' },
  { key: 'emergency_contact', label: 'ผู้ติดต่อฉุกเฉิน' },
  { key: 'date_of_birth', label: 'วันเกิด' },
  { key: 'gender', label: 'เพศ' },
  { key: 'religion', label: 'ศาสนา' },
  { key: 'marital_status', label: 'สถานภาพสมรส' },
];

const emptyForm = { name: '', redirect_uris: '', allowed_scopes: [] };

const DEFAULT_CLAIMS = [
  'รหัสผู้ใช้ถาวร (ใช้อ้างอิงตัวตนคงที่ ไม่เปลี่ยนแม้ข้อมูลอื่นจะแก้ไข)',
  'Username',
  'รหัสพนักงาน',
  'ชื่อ-นามสกุล (ทั้งภาษาอังกฤษและภาษาไทย)',
  'แผนก',
  'ตำแหน่ง',
  'สิทธิ์การใช้งานในระบบ (system role) ที่พนักงานคนนั้นมี',
  'สถานะการทำงาน (ยังทำงานอยู่ หรือลาออกแล้ว)',
];

// ตัวอย่างโค้ดฝั่งเว็บปลายทาง (Node.js/Express) — เนื้อหาตรงกับไฟล์จริงใน
// docs/coreplan-sso-client/ ของ repo นี้ ให้ดาวน์โหลด/คัดลอกไปวางในโปรเจกต์ปลายทางได้เลย
const STARTER_LOGIN_JS = `const crypto = require('crypto');
const coreplanConfig = require('./coreplan-credentials.json');

// Call this from your "Login with CorePlan" button's route handler.
// Store \`state\` in the user's session — you'll need to compare it against
// the \`state\` query param CorePlan sends back to \`callback.js\`, to prevent CSRF.
// Reads client_id / authorize_url / redirect_uri straight out of the
// credentials JSON you downloaded from CorePlan's admin UI (drop that file
// next to this one as \`coreplan-credentials.json\` — no separate env vars
// needed, same idea as a Firebase config file).
function buildAuthorizeUrl() {
  const state = crypto.randomBytes(16).toString('hex');
  const url = new URL(coreplanConfig.authorize_url);
  url.searchParams.set('client_id', coreplanConfig.client_id);
  url.searchParams.set('redirect_uri', coreplanConfig.redirect_uri);
  url.searchParams.set('state', state);
  return { url: url.toString(), state };
}

module.exports = { buildAuthorizeUrl };
`;

const STARTER_VERIFY_JS = `const jwt = require('jsonwebtoken');
const axios = require('axios');
const coreplanConfig = require('./coreplan-credentials.json');

// The credentials JSON only gives you \`public_key_url\`, not the PEM itself —
// GET {public_key_url} returns {"public_key": "<PEM string>"}. Fetch it once
// (e.g. on app startup) and cache it — do not re-fetch per request. The key
// never changes unless CorePlan rotates its signing key.
let cachedPublicKey = null;
async function getCorePlanPublicKey() {
  if (cachedPublicKey) return cachedPublicKey;
  const res = await axios.get(coreplanConfig.public_key_url);
  cachedPublicKey = res.data.public_key;
  return cachedPublicKey;
}

function verifyCoreplanToken(token, publicKeyPem) {
  return jwt.verify(token, publicKeyPem, { algorithms: ['RS256'] });
}

module.exports = { verifyCoreplanToken, getCorePlanPublicKey };
`;

const STARTER_CALLBACK_JS = `const axios = require('axios');
const coreplanConfig = require('./coreplan-credentials.json');
const { verifyCoreplanToken, getCorePlanPublicKey } = require('./verifyToken');

// Express route handler for your redirect_uri, e.g.:
//   app.get('/auth/coreplan/callback', handleCoreplanCallback);
// All config (client_id, client_secret, token_url, redirect_uri, ...) is read
// straight from coreplan-credentials.json — nothing to pass in.
async function handleCoreplanCallback(req, res) {
  const { code, state, error } = req.query;

  if (error) {
    return res.redirect('/login?error=coreplan_denied');
  }
  if (!code || state !== req.session.coreplanState) {
    return res.status(400).send('Invalid or missing state — possible CSRF, aborting.');
  }

  try {
    const tokenRes = await axios.post(coreplanConfig.token_url, {
      client_id: coreplanConfig.client_id,
      client_secret: coreplanConfig.client_secret,
      code,
      redirect_uri: coreplanConfig.redirect_uri,
    });

    const publicKey = await getCorePlanPublicKey();
    const claims = verifyCoreplanToken(tokenRes.data.access_token, publicKey);

    // claims.sub is CorePlan's stable per-user id — use it as the foreign key
    // when creating/looking up the matching user record in your own database.
    // Replace this block with your app's actual user upsert + session logic.
    req.session.user = {
      corePlanId: claims.sub,
      username: claims.username,
      firstname: claims.firstname,
      lastname: claims.lastname,
      department: claims.department,
      role: claims.role,
    };

    return res.redirect('/');
  } catch (err) {
    // Token exchange failed (network error, bad/expired code, invalid signature, etc).
    // In Express 4, an unhandled rejection here would leave the request hanging with
    // no response — always catch and reply. Don't leak \`err\` to the client.
    console.error('CorePlan callback failed:', err);
    return res.status(502).send('CorePlan login failed — please try again.');
  }
}

module.exports = { handleCoreplanCallback };
`;

const STARTER_WIRING_JS = `const { buildAuthorizeUrl } = require('./coreplan-auth/login');
const { handleCoreplanCallback } = require('./coreplan-auth/callback');

// ไม่ต้องตั้งค่า env var แยกเลย — ทุกอย่างอ่านมาจาก coreplan-credentials.json
// ที่วางไว้ในโฟลเดอร์ coreplan-auth/ โดยตรง (ดูหัวข้อ 8.1)

// route เริ่ม login — ใช้เป็นปลายทางของปุ่ม "Login with CorePlan"
app.get('/login/coreplan', (req, res) => {
  const { url, state } = buildAuthorizeUrl();
  req.session.coreplanState = state;
  res.redirect(url);
});

// route รับผลกลับ — ต้องตรงกับ Redirect URI ที่ลงทะเบียนไว้เป๊ะ
app.get('/auth/coreplan/callback', handleCoreplanCallback);
`;

function downloadTextFile(filename, content) {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    showAlert('คัดลอกโค้ดแล้ว วางในไฟล์ปลายทางได้เลย');
  } catch {
    showAlert('คัดลอกอัตโนมัติไม่สำเร็จ กรุณาเลือกโค้ดแล้วคัดลอกด้วยตนเอง');
  }
}

function CodeSnippet({ filename, description, code }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2" fontWeight="bold">{filename}</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<ContentCopyIcon fontSize="small" />} onClick={() => copyText(code)}>คัดลอก</Button>
          <Button size="small" startIcon={<DownloadIcon fontSize="small" />} onClick={() => downloadTextFile(filename, code)}>ดาวน์โหลด</Button>
        </Stack>
      </Stack>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{description}</Typography>
      )}
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          bgcolor: 'grey.900',
          color: 'grey.100',
          borderRadius: 1,
          fontSize: 12,
          fontFamily: 'monospace',
          overflowX: 'auto',
          whiteSpace: 'pre',
        }}
      >
        {code}
      </Box>
    </Box>
  );
}

export default function DeveloperOAuthApps() {
  const apiBase = String(process.env.REACT_APP_API_URL || '').trim();
  const frontendOrigin = window.location.origin;

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [credentialDialog, setCredentialDialog] = useState(null); // { name, client_id, client_secret }

  const [loginHistoryApp, setLoginHistoryApp] = useState(null);
  const [loginHistoryRows, setLoginHistoryRows] = useState([]);

  const [docsOpen, setDocsOpen] = useState(false);

  const [deleteApp, setDeleteApp] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const fetchApps = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${apiBase}/oauth/apps`);
      setApps(data);
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'โหลดรายการ App ไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  const openCreateForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEditForm = (app) => {
    setEditingId(app.id);
    setForm({
      name: app.name,
      redirect_uris: (app.redirect_uris || []).join('\n'),
      allowed_scopes: app.allowed_scopes || [],
    });
    setFormOpen(true);
  };

  const toggleScope = (key) => {
    setForm((f) => ({
      ...f,
      allowed_scopes: f.allowed_scopes.includes(key)
        ? f.allowed_scopes.filter((k) => k !== key)
        : [...f.allowed_scopes, key],
    }));
  };

  const submitForm = async () => {
    const payload = {
      name: form.name,
      redirect_uris: form.redirect_uris.split('\n').map((s) => s.trim()).filter(Boolean),
      allowed_scopes: form.allowed_scopes,
    };
    try {
      if (editingId) {
        await api.put(`${apiBase}/oauth/apps/${editingId}`, payload);
        setFormOpen(false);
        fetchApps();
      } else {
        const { data } = await api.post(`${apiBase}/oauth/apps`, payload);
        setFormOpen(false);
        fetchApps();
        setCredentialDialog(data);
      }
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'บันทึกไม่สำเร็จ');
    }
  };

  const toggleActive = async (app) => {
    try {
      await api.put(`${apiBase}/oauth/apps/${app.id}`, { active: !app.active });
      fetchApps();
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'บันทึกไม่สำเร็จ');
    }
  };

  const openDeleteConfirm = (app) => {
    setDeleteApp(app);
    setDeleteConfirmText('');
  };

  const confirmDelete = async () => {
    if (!deleteApp || deleteConfirmText !== deleteApp.name) return;
    setDeleting(true);
    try {
      await api.delete(`${apiBase}/oauth/apps/${deleteApp.id}`);
      setDeleteApp(null);
      fetchApps();
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'ลบ App ไม่สำเร็จ');
    } finally {
      setDeleting(false);
    }
  };

  const regenerateSecret = async (app) => {
    try {
      const { data } = await api.post(`${apiBase}/oauth/apps/${app.id}/regenerate-secret`);
      setCredentialDialog(data);
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'สร้าง secret ใหม่ไม่สำเร็จ');
    }
  };

  const viewCredentials = async (app) => {
    try {
      const { data } = await api.get(`${apiBase}/oauth/apps/${app.id}`);
      setCredentialDialog(data);
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'โหลด credential ไม่สำเร็จ');
    }
  };

  const openLoginHistory = async (app) => {
    setLoginHistoryApp(app);
    try {
      const { data } = await api.get(`${apiBase}/oauth/apps/${app.id}/logins`);
      setLoginHistoryRows(data);
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'โหลดประวัติ login ไม่สำเร็จ');
    }
  };

  const downloadCredentials = (cred) => {
    // apiBase (REACT_APP_API_URL) is a relative path in production (e.g. "/api/v1"),
    // meaningless outside this browser session — resolve it against frontendOrigin
    // so the downloaded JSON always contains absolute URLs.
    const apiAbsoluteBase = /^https?:\/\//.test(apiBase) ? apiBase : `${frontendOrigin}${apiBase}`;
    const payload = {
      client_id: cred.client_id,
      client_secret: cred.client_secret,
      // Destination sites can register more than one redirect_uri (e.g. staging + prod) —
      // the downloaded file can only carry one, so default to the first registered and let
      // the destination team hand-edit the file for a different environment.
      redirect_uri: (cred.redirect_uris || [])[0] || '',
      authorize_url: `${frontendOrigin}/oauth/authorize`,
      token_url: `${apiAbsoluteBase}/oauth/token`,
      public_key_url: `${apiAbsoluteBase}/oauth/public-key`,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Fixed filename (not per-app) — matches the starter kit's `require('./coreplan-credentials.json')`,
    // one file dropped directly into the destination project, no renaming needed.
    a.download = 'coreplan-credentials.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title="OAuth Apps — Login with CorePlan"
        subheader="จัดการ App ที่ให้เว็บอื่นล็อกอินผ่าน CorePlan"
        action={(
          <Stack direction="row" spacing={1}>
            <Button startIcon={<MenuBookIcon />} variant="outlined" onClick={() => setDocsOpen(true)}>วิธีใช้งาน</Button>
            <Button startIcon={<AddIcon />} variant="contained" onClick={openCreateForm}>สร้าง App</Button>
          </Stack>
        )}
      />
      <CardContent>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ชื่อ</TableCell>
                <TableCell>client_id</TableCell>
                <TableCell>Scope เพิ่มเติม</TableCell>
                <TableCell>สถานะ</TableCell>
                <TableCell align="right">การจัดการ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apps.map((app) => (
                <TableRow key={app.id}>
                  <TableCell>{app.name}</TableCell>
                  <TableCell><code>{app.client_id}</code></TableCell>
                  <TableCell>
                    {(app.allowed_scopes || []).map((k) => (
                      <Chip key={k} label={OPTIONAL_SCOPES.find((s) => s.key === k)?.label || k} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Switch checked={!!app.active} onChange={() => toggleActive(app)} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="ประวัติ login">
                      <IconButton size="small" onClick={() => openLoginHistory(app)}><HistoryIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="ดูและดาวน์โหลด credential">
                      <IconButton size="small" onClick={() => viewCredentials(app)}><DownloadIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="สร้าง secret ใหม่">
                      <IconButton size="small" onClick={() => regenerateSecret(app)}><RefreshIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    {!app.active && (
                      <Tooltip title="ลบ App (ปิดใช้งานแล้วเท่านั้นถึงลบได้)">
                        <IconButton size="small" color="error" onClick={() => openDeleteConfirm(app)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    <Button size="small" onClick={() => openEditForm(app)}>แก้ไข</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && apps.length === 0 && (
                <TableRow><TableCell colSpan={5}><Typography color="text.secondary" align="center">ยังไม่มี App</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      {/* Create/Edit form */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'แก้ไข App' : 'สร้าง App ใหม่'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="ชื่อ App" fullWidth value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            <TextField
              label="Redirect URIs (1 บรรทัดต่อ 1 URL)"
              fullWidth
              multiline
              minRows={2}
              value={form.redirect_uris}
              onChange={(e) => setForm((f) => ({ ...f, redirect_uris: e.target.value }))}
            />
            <Typography variant="subtitle2">ข้อมูลเพิ่มเติมที่ App นี้ขอได้ (นอกเหนือจากข้อมูลพื้นฐาน)</Typography>
            <FormGroup>
              {OPTIONAL_SCOPES.map((s) => (
                <FormControlLabel
                  key={s.key}
                  control={<Checkbox checked={form.allowed_scopes.includes(s.key)} onChange={() => toggleScope(s.key)} />}
                  label={s.label}
                />
              ))}
            </FormGroup>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>ยกเลิก</Button>
          <Button variant="contained" onClick={submitForm}>บันทึก</Button>
        </DialogActions>
      </Dialog>

      {/* Show credentials once (create or regenerate) */}
      <Dialog open={!!credentialDialog} onClose={() => setCredentialDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Credential ของ App</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
            กลับมาดูและดาวน์โหลด credential ชุดนี้ซ้ำได้ทุกเมื่อผ่านไอคอนดาวน์โหลดในตาราง — ไม่จำเป็นต้องคัดลอกเก็บไว้เองตอนนี้
          </Typography>
          {credentialDialog && (
            <Stack spacing={1}>
              <TextField label="client_id" value={credentialDialog.client_id} InputProps={{ readOnly: true }} fullWidth />
              <TextField label="client_secret" value={credentialDialog.client_secret} InputProps={{ readOnly: true }} fullWidth />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<DownloadIcon />} variant="contained" onClick={() => downloadCredentials(credentialDialog)}>
            Download JSON
          </Button>
          <Button onClick={() => setCredentialDialog(null)}>ปิด</Button>
        </DialogActions>
      </Dialog>

      {/* Login history */}
      <Dialog open={!!loginHistoryApp} onClose={() => setLoginHistoryApp(null)} maxWidth="sm" fullWidth>
        <DialogTitle>ประวัติ login — {loginHistoryApp?.name}</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>พนักงาน</TableCell>
                <TableCell>Login ล่าสุด</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loginHistoryRows.map((r) => (
                <TableRow key={r.employee_id}>
                  <TableCell>{r.employee_name}</TableCell>
                  <TableCell>{r.last_login_at ? new Date(r.last_login_at).toLocaleString('th-TH') : '-'}</TableCell>
                </TableRow>
              ))}
              {loginHistoryRows.length === 0 && (
                <TableRow><TableCell colSpan={2}><Typography color="text.secondary" align="center">ยังไม่มีใคร login ผ่าน App นี้</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoginHistoryApp(null)}>ปิด</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation — GitHub-repo-delete style: must type the exact app name to enable the button */}
      <Dialog open={!!deleteApp} onClose={() => setDeleteApp(null)} maxWidth="sm" fullWidth>
        <DialogTitle>ลบ App — {deleteApp?.name}</DialogTitle>
        <DialogContent>
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            การลบนี้ถาวร กู้คืนไม่ได้ — client_id/client_secret ของ App นี้จะใช้ล็อกอินไม่ได้อีกทันที และประวัติ login ทั้งหมดของ App นี้จะถูกลบไปด้วย
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            พิมพ์ชื่อ App <strong>{deleteApp?.name}</strong> ให้ตรงทุกตัวอักษรเพื่อยืนยัน
          </Typography>
          <TextField
            fullWidth
            size="small"
            autoFocus
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={deleteApp?.name || ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteApp(null)}>ยกเลิก</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!deleteApp || deleteConfirmText !== deleteApp.name || deleting}
            onClick={confirmDelete}
          >
            {deleting ? 'กำลังลบ...' : 'ลบ App นี้ถาวร'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Usage guide */}
      <Dialog open={docsOpen} onClose={() => setDocsOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>วิธีใช้งาน — Login with CorePlan (OAuth Apps)</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>1. ระบบนี้คืออะไร</Typography>
              <Typography variant="body2" color="text.secondary">
                ระบบนี้ทำให้ CorePlan เป็นศูนย์กลางการล็อกอินให้เว็บอื่นในบริษัทใช้งานได้ ("Login with CorePlan")
                เว็บปลายทางที่ต้องการให้พนักงานล็อกอินผ่าน CorePlan แทนการสร้างระบบ username/password แยกของตัวเอง
                จะต้องมี &quot;App&quot; ที่ลงทะเบียนไว้ในหน้านี้ก่อน 1 รายการต่อ 1 เว็บ — App แต่ละตัวจะได้รับกุญแจเฉพาะตัว (client_id / client_secret)
                ที่ใช้ยืนยันว่าคำขอ login มาจากเว็บนั้นจริง และกำหนดได้ว่าเว็บนั้นจะเห็นข้อมูลอะไรของพนักงานได้บ้าง
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>2. ขั้นตอนสร้าง App ใหม่ให้เว็บอื่น</Typography>
              <List dense sx={{ listStyleType: 'decimal', pl: 3, '& .MuiListItem-root': { display: 'list-item', pl: 0 } }}>
                <ListItem><ListItemText primary="กดปุ่ม &quot;สร้าง App&quot; มุมขวาบน" /></ListItem>
                <ListItem><ListItemText
                  primary="ใส่ &quot;ชื่อ App&quot;"
                  secondary="ใช้เป็นชื่อที่พนักงานจะเห็นตอนหน้าจอถามอนุญาต (เช่น &quot;App นี้ขอเข้าถึงข้อมูลของคุณ&quot;) ควรตั้งชื่อให้พนักงานเข้าใจว่าเป็นเว็บอะไร"
                /></ListItem>
                <ListItem><ListItemText
                  primary="ใส่ &quot;Redirect URIs&quot; — ที่อยู่หน้าเว็บปลายทางที่จะรับผลการล็อกอินกลับไป"
                  secondary={'ใส่ได้หลายอันโดยขึ้นบรรทัดใหม่ต่ออัน ต้องเป็นลิงก์เว็บที่ขึ้นต้นด้วย http:// หรือ https:// เท่านั้น (ระบบจะปฏิเสธรูปแบบอื่นทั้งหมดเพื่อความปลอดภัย) — ที่อยู่นี้ต้องตรงกับที่เว็บปลายทางส่งมาขอตอนล็อกอินทุกตัวอักษร ไม่งั้นระบบจะปฏิเสธการล็อกอิน'}
                /></ListItem>
                <ListItem><ListItemText
                  primary="เลือก &quot;ข้อมูลเพิ่มเติมที่ App นี้ขอได้&quot;"
                  secondary="ติ๊กเฉพาะข้อมูลอ่อนไหวที่เว็บนั้นจำเป็นต้องใช้จริงๆ เท่านั้น (ดูรายละเอียดหัวข้อที่ 3) ถ้าไม่ติ๊กอะไรเลย เว็บนั้นจะได้รับแค่ข้อมูลพื้นฐานเท่านั้น"
                /></ListItem>
                <ListItem><ListItemText
                  primary="กด &quot;บันทึก&quot;"
                  secondary="ระบบจะสร้าง client_id และ client_secret ให้ทันที และแสดงในหน้าต่างถัดไป (ดูหัวข้อที่ 4 — กลับมาดูซ้ำทีหลังได้เสมอ ไม่ใช่แค่ครั้งเดียว)"
                /></ListItem>
              </List>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>3. ข้อมูลที่ส่งให้เว็บปลายทาง (Claims / Scope)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                ทุก App จะได้รับข้อมูลพื้นฐานชุดนี้เสมอ ไม่ต้องขอเพิ่ม และปิดไม่ได้:
              </Typography>
              <List dense sx={{ listStyleType: 'disc', pl: 3, mb: 2, '& .MuiListItem-root': { display: 'list-item', pl: 0, py: 0.25 } }}>
                {DEFAULT_CLAIMS.map((c) => (
                  <ListItem key={c}><ListItemText primary={c} primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                ))}
              </List>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                ส่วนข้อมูลด้านล่างนี้เป็นข้อมูลอ่อนไหว <strong>จะไม่ถูกส่งให้เว็บไหนโดยอัตโนมัติ</strong> ต้องมาติ๊กเปิดเฉพาะรายการที่ App นั้นจำเป็นต้องใช้จริงเท่านั้น
                (แก้ไขทีหลังได้ตลอดผ่านปุ่ม &quot;แก้ไข&quot; ของแต่ละ App):
              </Typography>
              <List dense sx={{ listStyleType: 'disc', pl: 3, '& .MuiListItem-root': { display: 'list-item', pl: 0, py: 0.25 } }}>
                {OPTIONAL_SCOPES.map((s) => (
                  <ListItem key={s.key}><ListItemText primary={s.label} primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                ))}
              </List>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                หมายเหตุ: ถ้าไปเพิ่ม scope ให้ App ที่มีคนเคย login ผ่านไปแล้ว พนักงานที่เคย login ไปแล้วจะยังไม่ถูกถามซ้ำในการ login ครั้งถัดไป — คือได้รับสิทธิ์เพิ่มขึ้นทันทีโดยไม่มีการถามยืนยันซ้ำ
                ควรพิจารณาให้รอบคอบก่อนเพิ่ม scope ให้ App ที่ใช้งานจริงแล้ว
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>4. Credential (client_id / client_secret) และปุ่ม Download JSON</Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                หลังสร้าง App (หรือกดปุ่ม &quot;สร้าง secret ใหม่&quot;) ระบบจะโชว์ค่า 2 ตัวนี้:
                <List dense sx={{ listStyleType: 'disc', pl: 3, '& .MuiListItem-root': { display: 'list-item', pl: 0, py: 0.25 } }}>
                  <ListItem><ListItemText primary="client_id — เหมือน &quot;ชื่อบัญชี&quot; ของ App นั้น ไม่ใช่ความลับ ดูได้จากตารางรายการ App ตลอดเวลา" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                  <ListItem><ListItemText primary="client_secret — เหมือน &quot;รหัสผ่าน&quot; ของ App นั้น เป็นความลับ ควรระวังไม่ให้หลุดไปยังผู้อื่น — กดไอคอนดาวน์โหลดในตารางรายการ App เพื่อกลับมาดู/ดาวน์โหลดค่าเดิมซ้ำได้ทุกเมื่อ ไม่จำเป็นต้องกด &quot;สร้าง secret ใหม่&quot; เว้นแต่ต้องการเปลี่ยนค่าจริงๆ (ซึ่งจะทำให้ secret เก่าใช้ไม่ได้ทันที)" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                </List>
                กดปุ่ม <strong>&quot;Download JSON&quot;</strong> ในหน้าต่างนั้นเพื่อดาวน์โหลดไฟล์ตั้งค่าที่มีค่าทั้งหมดที่ทีมพัฒนาเว็บปลายทางต้องใช้ (client_id, client_secret, ลิงก์สำหรับพาไปหน้าล็อกอิน,
                ลิงก์สำหรับแลกรหัสเป็นข้อมูลตัวตน, ลิงก์สำหรับดึงกุญแจตรวจสอบลายเซ็น) — ส่งไฟล์นี้ให้ทีมพัฒนาเว็บปลายทางเก็บไว้เป็นความลับ (เช่นเดียวกับรหัสผ่าน ห้ามส่งผ่านช่องทางที่ไม่ปลอดภัยหรือแชร์สาธารณะ)
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>5. ขั้นตอนที่พนักงานเจอจริงตอนกด &quot;Login with CorePlan&quot;</Typography>
              <List dense sx={{ listStyleType: 'decimal', pl: 3, '& .MuiListItem-root': { display: 'list-item', pl: 0 } }}>
                <ListItem><ListItemText primary="พนักงานกดปุ่ม &quot;Login with CorePlan&quot; บนเว็บปลายทาง" /></ListItem>
                <ListItem><ListItemText primary="ระบบพาไปหน้าล็อกอินของ CorePlan (ถ้ายังไม่เคยล็อกอิน หรือ session หมดอายุ) — ถ้าล็อกอิน CorePlan ค้างอยู่แล้วจะข้ามขั้นตอนนี้ไปเลย" /></ListItem>
                <ListItem><ListItemText
                  primary="ถ้าเป็นการล็อกอินผ่าน App นี้ครั้งแรก จะขึ้นหน้า &quot;App นี้ขอเข้าถึงข้อมูลของคุณ&quot; ให้กด อนุญาต/ปฏิเสธ"
                  secondary="ถ้าเคยกดอนุญาตผ่าน App นี้มาก่อนแล้ว (ไม่ว่าจะกี่วันก่อนก็ตาม) ระบบจะข้ามหน้านี้ไปเลยในครั้งถัดๆ ไป เหมือนตอนล็อกอินด้วย Google ที่ปกติเจอหน้าขออนุญาตแค่ครั้งแรก"
                /></ListItem>
                <ListItem><ListItemText primary="ระบบพาพนักงานกลับไปที่เว็บปลายทางตาม Redirect URI ที่ตั้งไว้ พร้อมรหัสผ่านชั่วคราวที่ใช้ได้ครั้งเดียวและหมดอายุเร็วมาก (ประมาณ 1 นาที)" /></ListItem>
                <ListItem><ListItemText primary="เว็บปลายทาง (ฝั่งเซิร์ฟเวอร์ของเขา ไม่ใช่หน้าจอผู้ใช้) เอารหัสนั้นไปแลกเป็นข้อมูลตัวตนที่เซ็นรับรองแล้วกับ CorePlan โดยตรง — ขั้นตอนนี้พนักงานไม่เห็นและไม่ต้องทำอะไรเพิ่ม" /></ListItem>
                <ListItem><ListItemText primary="เว็บปลายทางรู้ตัวตนของพนักงานแล้ว (ชื่อ แผนก ตำแหน่ง ฯลฯ ตามที่อนุญาตไว้) และพาเข้าใช้งานเว็บนั้นต่อได้เลย โดยไม่ต้องสร้าง username/password ใหม่" /></ListItem>
              </List>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>6. ตารางรายการ App — แต่ละคอลัมน์คืออะไร</Typography>
              <List dense sx={{ listStyleType: 'disc', pl: 3, '& .MuiListItem-root': { display: 'list-item', pl: 0, py: 0.25 } }}>
                <ListItem><ListItemText primary="ชื่อ / client_id — ตามที่อธิบายไว้หัวข้อ 2 และ 4" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="Scope เพิ่มเติม — แสดงเป็น chip รายการข้อมูลอ่อนไหวที่ App นั้นได้รับอนุญาตให้ขอ (ตามหัวข้อ 3) ถ้าไม่มี chip แปลว่าได้แค่ข้อมูลพื้นฐาน" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="สถานะ — สวิตช์เปิด/ปิดการใช้งาน App นั้นทั้งหมด ปิดแล้วพนักงานจะล็อกอินผ่านเว็บนั้นไม่ได้ทันที (ใช้ตอนต้องการระงับการใช้งานเว็บใดเว็บหนึ่งเป็นการชั่วคราวหรือถาวร โดยไม่ต้องลบข้อมูล App ทิ้ง)" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="ไอคอนนาฬิกา (ประวัติ) — เปิดดูรายชื่อพนักงานที่เคยล็อกอินผ่าน App นี้ พร้อมวันเวลาล็อกอินล่าสุดของแต่ละคน" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="ไอคอนดาวน์โหลด — เปิดดู/ดาวน์โหลด credential (client_id, client_secret) ของ App นั้นซ้ำได้ทุกเมื่อ ไม่ต้องสร้าง secret ใหม่แค่เพื่อจะดูค่าเดิมอีกรอบ" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="ไอคอนรีเฟรช (สร้าง secret ใหม่) — ออก client_secret ใหม่ให้ App นั้น ใช้เมื่อ secret เดิมหลุด/รั่วไหล หรือสงสัยว่ามีคนอื่นเอาไปใช้ — secret เก่าจะใช้ไม่ได้ทันทีหลังกดปุ่มนี้ ต้องแจ้งทีมพัฒนาเว็บปลายทางให้เปลี่ยนไปใช้ค่าใหม่ด้วย ไม่งั้นเว็บนั้นจะล็อกอินไม่ได้" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="ปุ่มแก้ไข — แก้ชื่อ, Redirect URIs, และ scope เพิ่มเติมของ App นั้นได้ (แก้ไม่ได้: client_id, client_secret — ถ้าต้องการเปลี่ยน secret ให้ใช้ปุ่มสร้าง secret ใหม่แทน)" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
              </List>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>7. ข้อควรระวังด้านความปลอดภัย</Typography>
              <List dense sx={{ listStyleType: 'disc', pl: 3, '& .MuiListItem-root': { display: 'list-item', pl: 0, py: 0.25 } }}>
                <ListItem><ListItemText primary="client_secret ต้องเก็บเป็นความลับเสมอ ห้ามฝังในโค้ดฝั่งหน้าเว็บ (frontend) ที่ผู้ใช้ทั่วไปเปิดดูได้ ให้เก็บไว้ฝั่งเซิร์ฟเวอร์ของเว็บปลายทางเท่านั้น" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="ถ้าสงสัยว่า secret หลุด ให้กดสร้าง secret ใหม่ทันที ไม่ต้องรอ" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="ตั้ง Redirect URIs ให้ตรงกับเว็บปลายทางจริงเท่านั้น อย่าใส่ที่อยู่ที่ไม่แน่ใจว่าเป็นของใคร เพราะเป็นจุดที่ข้อมูลตัวตนจะถูกส่งไปให้" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="เปิด scope ข้อมูลอ่อนไหวเท่าที่ App นั้นจำเป็นต้องใช้จริงเท่านั้น ยิ่งเปิดน้อย ความเสี่ยงยิ่งต่ำถ้า App นั้นถูกโจมตีหรือถูกสวมสิทธิ์" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="เพื่อความสะดวก ระบบเก็บ client_secret ไว้ในรูปแบบที่กลับมาดู/ดาวน์โหลดซ้ำได้เสมอ (ต่างจากผู้ให้บริการ OAuth รายใหญ่ที่มักโชว์ครั้งเดียว) — เท่ากับว่าใครก็ตามที่เข้าถึงหน้านี้ได้ จะเห็น secret ของทุก App ได้ทั้งหมด จึงควรระวังเรื่องการเข้าถึงหน้าเมนูนี้เป็นพิเศษ" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
              </List>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>8. สำหรับทีมพัฒนาเว็บปลายทาง — ต้องตั้งค่า/เขียนโค้ดอะไรบ้าง</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                ส่วนนี้เป็นขั้นตอนทางเทคนิคที่ต้อง<strong>ส่งต่อให้ทีมพัฒนาของเว็บปลายทาง</strong>ทำ (ไม่ใช่ทำในหน้านี้) เว็บปลายทางต้องมีเซิร์ฟเวอร์ของตัวเอง
                (เช่น Node.js/Express) เพราะขั้นตอนแลกรหัสเป็นข้อมูลตัวตนต้องทำจากฝั่งเซิร์ฟเวอร์เท่านั้น
                — <strong>ห้ามทำจากฝั่งเบราว์เซอร์เด็ดขาด</strong> เพราะต้องใช้ client_secret ซึ่งเป็นความลับ ถ้าฝัง client_secret ไว้ในโค้ดฝั่งเบราว์เซอร์
                ผู้ใช้ทั่วไปจะเปิดดูได้ผ่านเครื่องมือนักพัฒนา (dev tools) ของเบราว์เซอร์ตัวเอง เท่ากับ secret หลุดทันที
              </Typography>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>8.1 วางไฟล์ credential ที่ดาวน์โหลดไว้ในโปรเจกต์ (ไม่ต้องตั้งค่า env var แยกเลย)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                กดไอคอนดาวน์โหลดในตารางรายการ App (หัวข้อ 4/6) จะได้ไฟล์ชื่อ <code>coreplan-credentials.json</code> ซึ่งมีค่าครบทุกตัวที่ต้องใช้อยู่ในไฟล์เดียว —
                เอาไฟล์นี้ไปวางในโปรเจกต์เว็บปลายทางตรงๆ (แนวคิดเดียวกับไฟล์ตั้งค่าของ Firebase ที่โหลดมาวางแล้วใช้ได้เลย ไม่ต้องพิมพ์ค่าไหนแยกเองอีก):
              </Typography>
              <List dense sx={{ listStyleType: 'disc', pl: 3, mb: 1, '& .MuiListItem-root': { display: 'list-item', pl: 0, py: 0.25 } }}>
                <ListItem><ListItemText primary="client_id, client_secret — กุญแจของ App นี้" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="redirect_uri — ที่อยู่หน้าเว็บของตัวเองที่รับผลการล็อกอินกลับ (ดึงมาจาก Redirect URI ตัวแรกที่ลงทะเบียนไว้กับ App นี้โดยอัตโนมัติ — ถ้ามีหลายที่อยู่และอยากใช้ตัวอื่น แก้ค่านี้ในไฟล์ JSON ได้ตรงๆ)" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="authorize_url, token_url, public_key_url — ลิงก์ของ CorePlan ที่ใช้ในแต่ละขั้นตอน" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
              </List>
              <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
                <strong>สำคัญ:</strong> ไฟล์นี้มี client_secret อยู่ข้างใน ต้องเพิ่มชื่อไฟล์ <code>coreplan-credentials.json</code> เข้าไปใน <code>.gitignore</code> ของโปรเจกต์ปลายทางด้วยเสมอ ห้าม commit ขึ้น git เด็ดขาด (เหมือนไฟล์ service account key ของ Firebase)
              </Typography>

              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>8.2 ไฟล์ที่ต้องสร้าง 3 ไฟล์ (โค้ดพร้อมใช้ กดคัดลอก/ดาวน์โหลดได้เลย)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                วางไว้ในโฟลเดอร์เดียวกับ <code>coreplan-credentials.json</code> เช่น <code>lib/coreplan-auth/</code> (ทั้ง 3 ไฟล์ <code>require()</code> ไฟล์ JSON นั้นตรงๆ) — เนื้อหาโค้ดด้านล่างนี้ใช้งานได้จริงทันที ไม่ต้องแก้ไข (ยกเว้นจุดที่มีคอมเมนต์บอกให้แก้)
              </Typography>

              <CodeSnippet
                filename="login.js"
                description="ไฟล์ที่ 1 — สร้างลิงก์พาไปหน้าล็อกอินของ CorePlan พร้อมรหัสกันการปลอมแปลง (state) เรียกใช้ตอนพนักงานกดปุ่ม 'Login with CorePlan'"
                code={STARTER_LOGIN_JS}
              />
              <CodeSnippet
                filename="verifyToken.js"
                description="ไฟล์ที่ 2 — ตรวจสอบลายเซ็นข้อมูลตัวตนที่ได้รับกลับมา ว่ามาจาก CorePlan จริงไม่ถูกปลอมแปลง (ใช้ไลบรารี jsonwebtoken ต้องติดตั้งเพิ่มถ้ายังไม่มี)"
                code={STARTER_VERIFY_JS}
              />
              <CodeSnippet
                filename="callback.js"
                description="ไฟล์ที่ 3 — รับผลตอนพนักงานถูกพากลับมาจาก CorePlan, ตรวจสอบ state, แลกรหัสเป็นข้อมูลตัวตน, ตรวจลายเซ็น แล้วสร้าง session ของเว็บตัวเอง (จุดที่ต้องแก้ให้ตรงกับระบบ user/session ของเว็บปลายทางเองมีคอมเมนต์บอกไว้ในโค้ด)"
                code={STARTER_CALLBACK_JS}
              />
              <CodeSnippet
                filename="ตัวอย่างการเชื่อมต่อ (wiring)"
                description="ตัวอย่างวิธีนำ 3 ไฟล์ข้างบนมาต่อเป็น route จริงในเว็บปลายทาง (ใส่ในไฟล์หลักของเซิร์ฟเวอร์ เช่น app.js/server.js) — เพิ่มปุ่ม 'Login with CorePlan' บนหน้าล็อกอินให้ชี้ไปที่ /login/coreplan"
                code={STARTER_WIRING_JS}
              />

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                ถ้าเว็บปลายทางไม่ได้ใช้ Node.js/Express ไม่เป็นไร — เขียนใหม่ในภาษา/เทคโนโลยีของตัวเองได้ตามสเปกในหัวข้อ 10
                (เรียก URL ตามลำดับ: authorize_url → token_url → public_key_url โดยใช้ client_id/client_secret/redirect_uri จากไฟล์ JSON เดียวกัน)
                โดยไม่จำเป็นต้องใช้โค้ดตัวอย่างนี้ตรงๆ
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>9. ข้อมูลตัวตนที่ได้รับ (Token) — รายละเอียดทางเทคนิค</Typography>
              <List dense sx={{ listStyleType: 'disc', pl: 3, mb: 1, '& .MuiListItem-root': { display: 'list-item', pl: 0, py: 0.25 } }}>
                <ListItem><ListItemText
                  primary="รูปแบบ: JWT (JSON Web Token) เซ็นรับรองด้วยอัลกอริทึม RS256"
                  secondary="ต้องตรวจสอบลายเซ็นด้วย public key ที่ดึงมาจาก public_key_url เสมอ ก่อนเชื่อข้อมูลข้างในทุกครั้ง ห้ามอ่านข้อมูลจาก token โดยไม่ตรวจลายเซ็นก่อน เพราะใครก็สร้างข้อความหน้าตาคล้าย token ปลอมขึ้นมาได้ ถ้าไม่ตรวจลายเซ็นระบบจะแยกไม่ออกว่าเป็นของจริงหรือปลอม"
                /></ListItem>
                <ListItem><ListItemText
                  primary="อายุการใช้งาน: 5 นาทีเท่านั้น"
                  secondary="ตั้งใจให้สั้นมาก เพราะ token นี้มีไว้ใช้ &quot;ครั้งเดียวตอนล็อกอิน&quot; เพื่อรับรู้ตัวตนเท่านั้น ไม่ใช่ token ที่ใช้แนบไปกับทุก request ตลอดการใช้งาน — เว็บปลายทางต้องอ่านข้อมูลจาก token นี้แล้วสร้าง session/token ของตัวเองเก็บไว้ใช้ต่อ (ตามระบบ login เดิมที่เว็บนั้นมีอยู่แล้ว)"
                /></ListItem>
                <ListItem><ListItemText primary="ข้อมูลพื้นฐานที่มีอยู่ในทุก token เสมอ (ชื่อฟิลด์ในวงเล็บคือชื่อจริงที่ต้องใช้อ่านค่าในโค้ด):" /></ListItem>
              </List>
              <Box component="pre" sx={{ m: 0, mb: 1.5, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, fontSize: 12, fontFamily: 'monospace', overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
{`sub            รหัสผู้ใช้ถาวรของ CorePlan — ใช้ผูกกับผู้ใช้ในระบบของเว็บปลายทางเอง (foreign key)
username       ชื่อผู้ใช้ CorePlan
employee_id    รหัสพนักงาน
firstname      ชื่อ (อังกฤษ)
lastname       นามสกุล (อังกฤษ)
firstname_th   ชื่อ (ไทย)
lastname_th    นามสกุล (ไทย)
department     ชื่อแผนก
role           ชื่อตำแหน่ง
system_role    รายชื่อสิทธิ์การใช้งานในระบบ (array)
work_status    ยังทำงานอยู่หรือไม่ (true/false)
iss            ผู้ออก token เสมอคือ "coreplan"
aud            client_id ของ App ที่ขอ token นี้ — ควรเช็คว่าตรงกับ client_id ของตัวเอง
iat / exp      เวลาออก / เวลาหมดอายุของ token`}
              </Box>
              <Typography variant="body2" color="text.secondary">
                ส่วนข้อมูลอ่อนไหว (เช่น date_of_birth, salary, bank_account, tax_id, address, id_card_image, documents, emergency_contact,
                gender, religion, marital_status) จะปรากฏใน token ก็ต่อเมื่อ App นั้นถูกเปิด scope นั้นไว้เท่านั้น (ดูหัวข้อ 3) — เว็บปลายทาง
                <strong>ห้ามเขียนโค้ดโดยสมมติว่าฟิลด์เหล่านี้จะมีอยู่เสมอ</strong> ต้องเช็คก่อนใช้งานทุกครั้งว่ามีค่าจริงหรือไม่ (undefined ได้ถ้าไม่ได้ขอ scope นั้นไว้)
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>10. สเปก URL ดิบ (สำหรับสแตกที่ไม่ใช่ Node.js)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                ถ้าเว็บปลายทางไม่ได้ใช้ Node.js/Express ให้เขียนเรียก HTTP 3 จุดนี้เองตามลำดับ โดยใช้ client_id, client_secret, redirect_uri, authorize_url,
                token_url, public_key_url ทั้งหมดจากไฟล์ <code>coreplan-credentials.json</code> ที่ดาวน์โหลดไว้ (หัวข้อ 8.1) — ไม่ต้องไปหาค่าที่อื่นเพิ่ม:
              </Typography>
              <List dense sx={{ listStyleType: 'decimal', pl: 3, '& .MuiListItem-root': { display: 'list-item', pl: 0 } }}>
                <ListItem><ListItemText
                  primary={<code>GET {'{authorize_url}'}?client_id=&redirect_uri=&state=</code>}
                  secondary="พา browser ไปทั้งหน้า (ไม่ใช่เรียกแบบ fetch/XHR เบื้องหลัง) redirect_uri ต้องตรงกับที่ลงทะเบียนไว้เป๊ะ ไม่งั้นได้ 403 กลับมาแทน — ผลลัพธ์คือ browser จะถูกพากลับไปที่ redirect_uri?code=...&state=... (หรือ redirect_uri?error=access_denied&state=... ถ้าพนักงานกดปฏิเสธ)"
                /></ListItem>
                <ListItem><ListItemText
                  primary={<code>POST {'{token_url}'}</code>}
                  secondary={'เรียกจากฝั่งเซิร์ฟเวอร์เท่านั้น (ต้องใช้ client_secret) — ส่ง JSON: {"client_id","client_secret","code","redirect_uri"} (redirect_uri ต้องเป็นอันเดียวกับข้อ 1 เป๊ะ) — ได้ผลลัพธ์เป็น {"access_token","token_type":"Bearer","expires_in":300} เมื่อสำเร็จ — code ใช้ได้ครั้งเดียวและหมดอายุใน 60 วินาที ถ้าล้มเหลวจะได้ status ไม่ใช่ 200 พร้อม {"error":{"status","message"}}'}
                /></ListItem>
                <ListItem><ListItemText
                  primary={<code>GET {'{public_key_url}'}</code>}
                  secondary='คืนค่า {"public_key": "<PEM string>"} — ดึงครั้งเดียวตอนเริ่มระบบแล้ว cache ไว้ใช้ตรวจลายเซ็น token ทุกครั้ง ไม่ต้องดึงใหม่ทุก request (กุญแจนี้จะไม่เปลี่ยนจนกว่า CorePlan จะสร้างกุญแจใหม่)'
                /></ListItem>
              </List>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>11. Checklist ทดสอบการเชื่อมต่อ</Typography>
              <List dense sx={{ listStyleType: 'disc', pl: 3, '& .MuiListItem-root': { display: 'list-item', pl: 0, py: 0.25 } }}>
                <ListItem><ListItemText primary="กดปุ่ม Login with CorePlan บนเว็บปลายทาง แล้วต้องถูกพามาที่หน้าล็อกอิน/หน้าอนุญาตของ CorePlan (ไม่ใช่หน้า error)" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="กดอนุญาตแล้วต้องถูกพากลับไปที่เว็บปลายทางสำเร็จ พร้อม session/login ของเว็บนั้นใช้งานได้จริง" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="ลองกด Login with CorePlan ซ้ำรอบสอง — ต้องข้ามหน้าอนุญาตไปเลย (เพราะเคยอนุญาตแล้ว) ไม่ต้องกดอนุญาตซ้ำ" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="ลองกดปฏิเสธแทนอนุญาต — เว็บปลายทางต้องจัดการกรณีนี้ได้อย่างเหมาะสม (เช่น กลับไปหน้าล็อกอินปกติ ไม่ค้างหรือ error)" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="เช็คว่าข้อมูลที่เว็บปลายทางได้รับ (ชื่อ แผนก ตำแหน่ง) ตรงกับพนักงานที่ล็อกอินจริง และไม่มีข้อมูล scope ที่ไม่ได้ขอไว้หลุดออกไป" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                <ListItem><ListItemText primary="กลับมาเช็คที่ไอคอนประวัติ (หัวข้อ 6) ในตารางของ App นี้ ว่าเห็นชื่อพนักงานที่เพิ่งทดสอบล็อกอินไปหรือไม่" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
              </List>
            </Box>

          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setDocsOpen(false)}>ปิด</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
